import type { View } from 'react-native'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useReducer,
  useRef,
} from 'react'

import type { VisualMediaPlayerProps } from './VisualMediaPlayer.types'
import { buildMediaErrorExtra } from '../../../utils/media-error'
import { addBreadcrumb, captureMessage } from '../../../utils/sentry'
import { resolveWebVideoAspectRatio } from '../utils/web-video-aspect-ratio'
import {
  ASPECT_RATIO_CHANGE_THRESHOLD,
  VisualMediaPlayerErrorFallback,
  VisualMediaPlayerFrame,
  VisualMediaPlayerLoadingOverlay,
} from './VisualMediaPlayerFrame'
import { VisualMediaPlayerOverlay } from './VisualMediaPlayerOverlay'

type WebkitVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
  webkitExitFullscreen?: () => void
  webkitDisplayingFullscreen?: boolean
}

type WebkitDocument = Document & {
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}

type PlayRequestOptions = {
  allowMutedAutoplayFallback: boolean
  reason: string
}

type PlayerState = {
  aspectRatio: number
  hasError: boolean
  isFullscreen: boolean
  isLoading: boolean
  isMuted: boolean
  isPlaying: boolean
}

type PlayerAction =
  | { type: 'reset' }
  | { type: 'setAspectRatio'; value: number }
  | { type: 'setError'; value: boolean }
  | { type: 'setFullscreen'; value: boolean }
  | { type: 'setLoading'; value: boolean }
  | { type: 'setMuted'; value: boolean }
  | { type: 'setPlaying'; value: boolean }

const HAVE_FUTURE_DATA = 3

function canPlayWithoutWaiting(video: HTMLVideoElement): boolean {
  return video.readyState >= HAVE_FUTURE_DATA || video.ended
}

function createInitialState(initialAspectRatio: number): PlayerState {
  return {
    aspectRatio: initialAspectRatio,
    hasError: false,
    isFullscreen: false,
    isLoading: true,
    isMuted: false,
    isPlaying: false,
  }
}

function playerReducer(state: PlayerState, action: PlayerAction): PlayerState {
  switch (action.type) {
    case 'reset':
      return createInitialState(state.aspectRatio)
    case 'setAspectRatio':
      return Math.abs(action.value - state.aspectRatio) <=
        ASPECT_RATIO_CHANGE_THRESHOLD
        ? state
        : { ...state, aspectRatio: action.value }
    case 'setError':
      return { ...state, hasError: action.value }
    case 'setFullscreen':
      return { ...state, isFullscreen: action.value }
    case 'setLoading':
      return { ...state, isLoading: action.value }
    case 'setMuted':
      return { ...state, isMuted: action.value }
    case 'setPlaying':
      return { ...state, isPlaying: action.value }
    default:
      return state
  }
}

function isFullscreenActive(
  container: HTMLElement,
  video: HTMLVideoElement,
): boolean {
  const webkitDoc = document as WebkitDocument
  const docFullscreenElement =
    document.fullscreenElement ?? webkitDoc.webkitFullscreenElement
  if (docFullscreenElement === container || docFullscreenElement === video) {
    return true
  }
  return (video as WebkitVideoElement).webkitDisplayingFullscreen === true
}

function isExpectedPlayInterruption(error: unknown): boolean {
  if (!(error instanceof DOMException)) return false

  return error.name === 'AbortError' || error.name === 'NotAllowedError'
}

// oxlint-disable-next-line react-doctor/no-giant-component -- Browser media events stay colocated to keep playback state transitions traceable.
function VisualMediaPlayerContent({
  source,
  contentType,
  aspectRatio,
  diagnostics,
  fallbackAspectRatio,
  onLoadingChange,
  onErrorChange,
  ref,
}: VisualMediaPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasResolvedInitialLoadRef = useRef(false)
  const lastReportedErrorSignatureRef = useRef<string | null>(null)
  const onLoadingChangeRef = useRef(onLoadingChange)
  const onErrorChangeRef = useRef(onErrorChange)
  const playRequestIdRef = useRef(0)
  const [state, dispatch] = useReducer(
    playerReducer,
    aspectRatio || fallbackAspectRatio,
    createInitialState,
  )

  const resolvedAspectRatio = state.aspectRatio || fallbackAspectRatio

  useImperativeHandle(
    ref,
    () => ({
      pause() {
        videoRef.current?.pause()
      },
    }),
    [],
  )

  useEffect(() => {
    onLoadingChangeRef.current = onLoadingChange
  }, [onLoadingChange])

  useEffect(() => {
    onErrorChangeRef.current = onErrorChange
  }, [onErrorChange])

  const emitLoadingChange = useCallback((loading: boolean) => {
    dispatch({ type: 'setLoading', value: loading })
    onLoadingChangeRef.current?.(loading)
  }, [])

  const emitErrorChange = useCallback((error: boolean) => {
    dispatch({ type: 'setError', value: error })
    onErrorChangeRef.current?.(error)
  }, [])

  const resolveInitialLoading = useCallback(() => {
    if (hasResolvedInitialLoadRef.current) return
    hasResolvedInitialLoadRef.current = true
    emitLoadingChange(false)
  }, [emitLoadingChange])

  const reportError = useCallback(
    (reason: string, rawError?: unknown) => {
      const diagnosticExtra: Record<string, unknown> = {
        assetContentType: contentType,
        assetFileName: diagnostics?.assetFileName ?? null,
        sessionId: diagnostics?.sessionId ?? null,
        sourceUri: source.uri,
        stepIndex: diagnostics?.stepIndex ?? null,
        stepTitle: diagnostics?.stepTitle ?? null,
        reason,
        ...buildMediaErrorExtra(rawError),
      }
      const signature = JSON.stringify({
        reason,
        rawErrorMessage: diagnosticExtra.rawErrorMessage ?? null,
        sourceUri: source.uri,
      })

      if (lastReportedErrorSignatureRef.current === signature) return
      lastReportedErrorSignatureRef.current = signature

      console.error('[VisualMediaPlayer] Playback failed', diagnosticExtra)
      captureMessage('Video playback failed', 'error', {
        feature: 'media',
        tags: { player: 'video-web', reason },
        extra: diagnosticExtra,
      })
    },
    [contentType, diagnostics, source.uri],
  )

  const requestPlay = useCallback(
    ({ allowMutedAutoplayFallback, reason }: PlayRequestOptions) => {
      const video = videoRef.current
      if (video == null) return

      const requestId = playRequestIdRef.current + 1
      playRequestIdRef.current = requestId

      video
        .play()
        .then(() => {
          if (playRequestIdRef.current !== requestId) return

          if (allowMutedAutoplayFallback && video.muted) {
            video.muted = false
            dispatch({ type: 'setMuted', value: false })
          }
        })
        .catch((rawError: unknown) => {
          if (playRequestIdRef.current !== requestId) return

          if (isExpectedPlayInterruption(rawError)) {
            if (allowMutedAutoplayFallback && video.paused) {
              video.muted = true
              dispatch({ type: 'setMuted', value: true })
              video.play().catch(() => {})
            }
            return
          }

          emitLoadingChange(false)
          emitErrorChange(true)
          dispatch({ type: 'setPlaying', value: false })
          reportError(reason, rawError)
        })
    },
    [emitErrorChange, emitLoadingChange, reportError],
  )

  useEffect(() => {
    hasResolvedInitialLoadRef.current = false
    lastReportedErrorSignatureRef.current = null
    playRequestIdRef.current += 1
    onLoadingChangeRef.current?.(true)
    onErrorChangeRef.current?.(false)
    addBreadcrumb('media', 'video_load_start', { sourceUri: source.uri })
  }, [source.uri])

  useEffect(() => {
    const video = videoRef.current
    if (video == null) return

    video.muted = true
    dispatch({ type: 'setMuted', value: true })
    requestPlay({
      allowMutedAutoplayFallback: true,
      reason: 'autoplay_rejected',
    })
  }, [requestPlay, source.uri])

  const handlePlaybackReady = useCallback(() => {
    resolveInitialLoading()
    emitLoadingChange(false)
  }, [emitLoadingChange, resolveInitialLoading])

  const handlePlaybackReadyIfBuffered = useCallback(() => {
    const video = videoRef.current
    if (video != null && !canPlayWithoutWaiting(video)) return

    handlePlaybackReady()
  }, [handlePlaybackReady])

  const handleCanPlay = useCallback(() => {
    handlePlaybackReadyIfBuffered()
    addBreadcrumb('media', 'video_ready', { sourceUri: source.uri })
  }, [handlePlaybackReadyIfBuffered, source.uri])

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current
    if (video == null) return

    dispatch({
      type: 'setAspectRatio',
      value: resolveWebVideoAspectRatio({
        fallbackAspectRatio,
        videoHeight: video.videoHeight,
        videoWidth: video.videoWidth,
      }),
    })
  }, [fallbackAspectRatio])

  const handleError = useCallback(() => {
    emitLoadingChange(false)
    emitErrorChange(true)
    addBreadcrumb('media', 'video_error', { sourceUri: source.uri })
    reportError('web_error')
  }, [emitErrorChange, emitLoadingChange, reportError, source.uri])

  const handlePlay = useCallback(() => {
    const video = videoRef.current
    dispatch({ type: 'setPlaying', value: true })
    emitErrorChange(false)
    if (video == null || canPlayWithoutWaiting(video)) {
      handlePlaybackReady()
      return
    }
    emitLoadingChange(true)
  }, [emitErrorChange, emitLoadingChange, handlePlaybackReady])

  const handlePlaying = useCallback(() => {
    dispatch({ type: 'setPlaying', value: true })
    handlePlaybackReady()
  }, [handlePlaybackReady])

  const handlePause = useCallback(() => {
    playRequestIdRef.current += 1
    resolveInitialLoading()
    emitLoadingChange(false)
    dispatch({ type: 'setPlaying', value: false })
  }, [emitLoadingChange, resolveInitialLoading])

  const handleEnded = useCallback(() => {
    resolveInitialLoading()
    dispatch({ type: 'setPlaying', value: false })
  }, [resolveInitialLoading])

  const handleWaiting = useCallback(() => {
    const video = videoRef.current
    if (video == null || video.paused || video.ended) return

    emitLoadingChange(true)
  }, [emitLoadingChange])

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current
    if (video == null || video.paused) return

    handlePlaybackReadyIfBuffered()
  }, [handlePlaybackReadyIfBuffered])

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current
    if (video == null) return
    if (!video.paused) {
      playRequestIdRef.current += 1
      video.pause()
    } else {
      requestPlay({
        allowMutedAutoplayFallback: false,
        reason: 'play_rejected',
      })
    }
  }, [requestPlay])

  const toggleMute = useCallback(() => {
    const video = videoRef.current
    if (video == null) return
    video.muted = !video.muted
    dispatch({ type: 'setMuted', value: video.muted })
  }, [])

  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current
    const video = videoRef.current
    if (container == null) return

    const webkitDoc = document as WebkitDocument
    const docFullscreenElement =
      document.fullscreenElement ?? webkitDoc.webkitFullscreenElement

    if (docFullscreenElement != null) {
      if (typeof document.exitFullscreen === 'function') {
        document.exitFullscreen().catch(() => {})
      } else if (typeof webkitDoc.webkitExitFullscreen === 'function') {
        webkitDoc.webkitExitFullscreen()
      } else if (video != null) {
        const wkVideo = video as WebkitVideoElement
        if (typeof wkVideo.webkitExitFullscreen === 'function') {
          wkVideo.webkitExitFullscreen()
        }
      }
      return
    }

    if (typeof container.requestFullscreen === 'function') {
      container.requestFullscreen().catch(() => {
        if (video != null) {
          const wkVideo = video as WebkitVideoElement
          if (typeof wkVideo.webkitEnterFullscreen === 'function') {
            try {
              wkVideo.webkitEnterFullscreen()
            } catch {
              // WebKit fullscreen not available
            }
          }
        }
      })
      return
    }

    if (video != null) {
      const wkVideo = video as WebkitVideoElement
      if (typeof wkVideo.webkitEnterFullscreen === 'function') {
        try {
          wkVideo.webkitEnterFullscreen()
        } catch {
          // WebKit fullscreen not available
        }
      }
    }
  }, [])

  useEffect(() => {
    const container = containerRef.current
    const video = videoRef.current

    const handleFullscreenChange = () => {
      if (container != null && video != null) {
        dispatch({
          type: 'setFullscreen',
          value: isFullscreenActive(container, video),
        })
      } else {
        dispatch({
          type: 'setFullscreen',
          value: document.fullscreenElement != null,
        })
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    if (video != null) {
      video.addEventListener('webkitbeginfullscreen', handleFullscreenChange)
      video.addEventListener('webkitendfullscreen', handleFullscreenChange)
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      )
      if (video != null) {
        video.removeEventListener(
          'webkitbeginfullscreen',
          handleFullscreenChange,
        )
        video.removeEventListener('webkitendfullscreen', handleFullscreenChange)
      }
    }
  }, [])

  if (state.hasError) {
    return <VisualMediaPlayerErrorFallback aspectRatio={resolvedAspectRatio} />
  }

  return (
    <VisualMediaPlayerFrame
      ref={containerRef as unknown as React.Ref<View>}
      aspectRatio={resolvedAspectRatio}
    >
      <video
        ref={videoRef}
        src={source.uri}
        loop
        playsInline
        preload="auto"
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handlePlaybackReadyIfBuffered}
        onCanPlay={handleCanPlay}
        onCanPlayThrough={handlePlaybackReadyIfBuffered}
        onTimeUpdate={handleTimeUpdate}
        onWaiting={handleWaiting}
        onStalled={handleWaiting}
        onError={handleError}
        onPlay={handlePlay}
        onPlaying={handlePlaying}
        onPause={handlePause}
        onEnded={handleEnded}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          cursor: 'pointer',
        }}
        onClick={togglePlayPause}
      />

      {state.isLoading && <VisualMediaPlayerLoadingOverlay />}

      <VisualMediaPlayerOverlay
        isFullscreen={state.isFullscreen}
        isMuted={state.isMuted}
        isPlaying={state.isPlaying}
        onToggleFullscreen={toggleFullscreen}
        onToggleMute={toggleMute}
      />
    </VisualMediaPlayerFrame>
  )
}

export function VisualMediaPlayer(props: VisualMediaPlayerProps) {
  return <VisualMediaPlayerContent key={props.source.uri} {...props} />
}
